import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Menu, Bell, User, ChevronDown, Cloud, WifiOff, AlertTriangle } from "lucide-react";
import { fetchSyncStatus } from "../../store/slices/syncSlice";


const Header = ({ title, onToggleSidebar }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const clinic = useSelector((state) => state.clinic.clinic);
  const sync = useSelector((state) => state.sync);
  const labName = clinic?.name || "Laboratory";
  const logoUrl = clinic?.logo_url || "/logo.png";

  const getRoleDisplayName = (role) => {
    const roleNames = {
      receptionist: "Receptionist",
      laboratorist: "Lab Technician",
      admin: "Administrator",
      patient: "Patient",
    };
    return roleNames[role] || role;
  };

  useEffect(() => {
    // Sync status endpoints are admin-only; skip polling for other roles.
    if (user?.role !== "admin") return;

    dispatch(fetchSyncStatus());
    const id = setInterval(() => {
      dispatch(fetchSyncStatus());
    }, 60000);
    return () => clearInterval(id);
  }, [dispatch, user?.role]);

  const syncStatus = sync?.status;
  const syncError = sync?.error || syncStatus?.lastError;
  const online = syncStatus?.online ?? syncStatus?.connection?.online;
  const pending = syncStatus?.pendingLocalChanges ?? syncStatus?.pendingChanges ?? 0;

  let syncLabel = "Sync: Unknown";
  let badgeClass = "bg-gray-100 text-gray-600";
  let dotClass = "bg-gray-400";
  let Icon = Cloud;

  if (sync.loadingStatus && !syncStatus) {
    syncLabel = "Sync: Checking…";
  } else if (syncError) {
    syncLabel = "Sync: Error";
    badgeClass = "bg-red-50 text-red-600";
    dotClass = "bg-red-500";
    Icon = AlertTriangle;
  } else if (online === false) {
    syncLabel = pending ? `Offline · ${pending} queued` : "Offline";
    badgeClass = "bg-amber-50 text-amber-700";
    dotClass = "bg-amber-500";
    Icon = WifiOff;
  } else if (online === true) {
    if (pending > 0) {
      syncLabel = `Online · ${pending} queued`;
      badgeClass = "bg-sky-50 text-sky-700";
      dotClass = "bg-sky-500";
    } else {
      syncLabel = "Online · Synced";
      badgeClass = "bg-emerald-50 text-emerald-700";
      dotClass = "bg-emerald-500";
    }
  }

  return (
    <header className="bg-white/80 backdrop-blur-lg shadow-sm border-b border-gray-200/50 sticky top-0 z-40">
      <div className="px-6 py-4">
        <div className="flex justify-between items-center">
          {/* Left Section */}
          <div className="flex items-center space-x-4">
            <button
              onClick={onToggleSidebar}
              className="lg:hidden p-2 rounded-xl hover:bg-gray-100 text-gray-600 hover:text-[#235F72] transition-all duration-200 hover:scale-110"
              aria-label="Toggle Menu"
            >
              <Menu className="w-6 h-6" />
            </button>
            <img
              src={logoUrl}
              alt={clinic?.name ? `${clinic.name} Logo` : "Clinic Logo"}
              className="w-10 h-10 rounded-lg object-cover border border-gray-200 bg-white"
              onError={(e) => { e.target.src = "/logo.png"; }}
              style={{ marginRight: 12 }}
            />
            <div className="flex flex-col">
              <h1 className="text-2xl font-bold bg-gradient-to-r from-[#235F72] to-[#1a4a5a] bg-clip-text text-transparent">
                {title === 'Laboratory Dashboard' || title === 'Laboratory Config' || title === 'Laboratory' ? labName : title}
              </h1>
              <p className="text-xs text-gray-500 mt-0.5">
                {new Date().toLocaleDateString("en-US", {
                  weekday: "long",
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </p>
            </div>
          </div>

          {/* Right Section */}
          <div className="flex items-center space-x-3">
            {/* Sync status (admin focus, visible to all) */}
            <button
              type="button"
              onClick={() => {
                if (user?.role === "admin") navigate("/admin/sync");
              }}
              className={`hidden sm:inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold border ${badgeClass} transition-all duration-200 hover:shadow-sm ${
                user?.role === "admin" ? "cursor-pointer" : "cursor-default"
              }`}
              title={
                user?.role === "admin"
                  ? "Open Sync Center"
                  : "Sync status"
              }
            >
              <span className={`w-2 h-2 rounded-full ${dotClass}`}></span>
              <Icon className="w-3 h-3" />
              <span>{syncLabel}</span>
            </button>

            {/* Notifications */}
            <button
              className="relative p-2.5 rounded-xl hover:bg-gray-100 text-gray-600 hover:text-[#235F72] transition-all duration-200 hover:scale-110"
              aria-label="Notifications"
            >
              <Bell className="w-5 h-5" />
              <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full ring-2 ring-white"></span>
            </button>

            {/* User Profile */}
            <div className="flex items-center space-x-3 pl-3 border-l border-gray-200">
              <div className="hidden md:flex flex-col items-end">
                <span className="text-sm font-semibold text-gray-800">
                  {user?.full_name}
                </span>
                <span className="text-xs text-gray-500">
                  {getRoleDisplayName(user?.role)}
                </span>
              </div>
              <div className="relative group">
                <button className="flex items-center space-x-2 p-1.5 rounded-xl hover:bg-gray-100 transition-all duration-200">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#36F1A2] to-[#00d084] flex items-center justify-center text-[#1a4a5a] font-bold shadow-lg">
                    <User className="w-5 h-5" />
                  </div>
                  <ChevronDown className="w-4 h-4 text-gray-500 hidden md:block" />
                </button>
                {/* Dropdown hint (can be expanded with actual dropdown menu) */}
              </div>
              <span className="hidden sm:inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold bg-gradient-to-r from-[#36F1A2] to-[#00d084] text-[#1a4a5a] shadow-md">
                {getRoleDisplayName(user?.role)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
